import { Fragment, useEffect, useRef, useState } from 'react'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'

import Spinner from '~/components/Spinner'
import ROUTES from '~/shared/constants/routes.const'
import { AppLanguage } from '~/types/locale'
import { isAuthenticated } from '~/auth/slices/auth.slice'
import { useAppSelector } from '~/redux/hooks'

import { useGetCurriculumCountriesQuery } from '../apis/curriculum.api'
import { CurriculumCountryCodes, CurriculumCountryNames } from '../constants/curriculum.const'
import {
  findUsCurriculumStateByName,
  getOrderedUsCurriculumStates,
  US_GROUPED_COUNTRY_CODES,
} from '../constants/usCurriculumStates.const'

import CurriculumDropdownItem from './CurriculumDropdownItem'
import CurriculumGroupItem from './CurriculumGroupItem'
import CurriculumStateItem from './CurriculumStateItem'

interface Props {
  handleOptionSelect: (chosenCurriculumName: string) => void
  isDark?: boolean
  isFooter?: boolean
  isMenu?: boolean
  currentCurriculum: string
  currentCurriculumCode: string
  isOnboarding?: boolean
  isLightNested?: boolean
}

export default function CurriculumDropdownOptions({
  handleOptionSelect,
  currentCurriculum,
  currentCurriculumCode,
  isDark,
  isFooter,
  isMenu,
  isOnboarding,
  isLightNested,
}: Props): React.ReactElement {
  const router = useRouter()
  const { data: curriculums, isLoading } = useGetCurriculumCountriesQuery()
  const authenticated = useAppSelector(isAuthenticated)
  const { t } = useTranslation('curriculum')
  // on the select-grade page `currentCurriculum` is derived from the URL and is still empty on the first render, so a once-computed initial value never caught
  // up and the group stayed collapsed for US users.
  const [userToggledUsaExpanded, setUserToggledUsaExpanded] = useState<boolean | null>(null)
  // Also keyed off the code, so a US user with no state still opens on the group they need.
  const isUsaExpanded =
    userToggledUsaExpanded ??
    (Boolean(findUsCurriculumStateByName(currentCurriculum)) ||
      US_GROUPED_COUNTRY_CODES.includes(currentCurriculumCode))

  const usStatesListRef = useRef<HTMLDivElement>(null)
  const availableCountryCodes = new Set((curriculums ?? []).map(({ countryCode }) => countryCode))
  const usStates = getOrderedUsCurriculumStates(availableCountryCodes)
  const currentUsStateIndex = usStates.findIndex(({ curriculumName }) => curriculumName === currentCurriculum)
  useEffect(() => {
    const list = usStatesListRef.current
    if (!isUsaExpanded || !list || currentUsStateIndex < 0) return
    const row = list.children[currentUsStateIndex] as HTMLElement | undefined
    if (row) list.scrollTop = row.offsetTop - list.clientHeight / 2 + row.clientHeight / 2
  }, [isUsaExpanded, currentUsStateIndex])

  const LanguageSelections = curriculums?.filter(
    (curriculum) => curriculum.countryCode === AppLanguage.NO || curriculum.country === 'USA',
  )

  const isCampaignPage = router.pathname.includes(ROUTES.CAMPAIGNS)
  const isPartnerPage = router.pathname.includes(ROUTES.PARTNER)
  const AvailableCurriculums = (isCampaignPage || isPartnerPage) && !authenticated ? LanguageSelections : curriculums

  if (isLoading || !curriculums) {
    return (
      <div
        className={`flex ${isFooter ? 'px-2 lg:px-0 lg:justify-center' : 'justify-center'} ${
          isFooter ? 'py-4' : 'py-1'
        }`}
      >
        <Spinner color={isFooter && !isLightNested ? 'white' : 'purple'} size="large" />
      </div>
    )
  }

  const isDarkMenu = isDark && isMenu
  const darkMenuTextClassName = isDarkMenu || isFooter ? 'text-xs font-normal text-white-strong' : undefined
  const isNestedList = isDarkMenu || isFooter || isLightNested

  if (isNestedList) {
    const topLevelCountries = (curriculums ?? []).filter(
      ({ countryCode }) => !US_GROUPED_COUNTRY_CODES.includes(countryCode),
    )

    const dividerClassName = `h-px w-full ${isLightNested ? 'bg-gray-extra-light' : 'bg-white-faint'}`

    return (
      <div aria-label="Curriculum options" className="w-full flex flex-col items-start" role="listbox">
        <CurriculumGroupItem
          curriculumCountryCode={CurriculumCountryCodes.USA}
          curriculumName={CurriculumCountryNames.USA}
          hint={t('usaCurriculumHint')}
          isExpanded={isUsaExpanded}
          isLight={isLightNested}
          onToggle={() => setUserToggledUsaExpanded(!isUsaExpanded)}
        />
        {isUsaExpanded && (
          <div className="relative w-full">
            <div className={dividerClassName} />
            <div className="max-h-55 w-full overflow-y-auto overscroll-contain pb-5" ref={usStatesListRef}>
              {usStates.map(({ curriculumName, label }) => (
                <CurriculumStateItem
                  isCurrentCurriculum={curriculumName === currentCurriculum}
                  isLight={isLightNested}
                  key={label}
                  label={label}
                  onClick={curriculumName ? () => handleOptionSelect(curriculumName) : undefined}
                />
              ))}
            </div>
            <div
              className={`pointer-events-none absolute inset-x-0 bottom-0 h-10 bg-gradient-to-b from-transparent ${
                isLightNested ? 'to-white' : 'to-purple-abyss'
              }`}
            />
          </div>
        )}
        {topLevelCountries.map(({ country, countryCode }) => {
          const isCurrent = country === currentCurriculum && countryCode === currentCurriculumCode
          return (
            <Fragment key={`${country}-${countryCode}`}>
              <div className={dividerClassName} />
              <CurriculumDropdownItem
                currentCurriculumCode={currentCurriculumCode}
                curriculumCountryCode={countryCode}
                curriculumName={country}
                isCurrentCurriculum={isCurrent}
                isDark={!isLightNested}
                isMenu
                onClick={handleOptionSelect}
                rowClassName={`w-full px-3.5 py-3 duration-300 ${isCurrent ? 'bg-orange-faint' : ''} ${
                  isLightNested ? 'hover:bg-gray-light' : 'hover:bg-orange-faint'
                }`}
                textClassName={
                  isCurrent
                    ? `text-xs ${isLightNested ? 'font-semibold text-orange-dark' : 'font-normal text-orange-light'}`
                    : isLightNested
                    ? 'text-xs font-normal text-gray-davy'
                    : darkMenuTextClassName
                }
              />
            </Fragment>
          )
        })}
      </div>
    )
  }

  return (
    <div
      aria-label="Curriculum options"
      className={`w-full flex flex-col items-start ${
        isDarkMenu ? 'divide-y divide-white-faint' : 'rounded-2 p-2 gap-2.5'
      }`}
      role="listbox"
    >
      {AvailableCurriculums?.map((curriculumInfo) => {
        const { country, countryCode } = curriculumInfo
        const isCurrent = country === currentCurriculum && countryCode === currentCurriculumCode
        if (isCurrent) {
          if (isFooter) {
            return (
              <CurriculumDropdownItem
                currentCurriculumCode={currentCurriculumCode}
                curriculumCountryCode={countryCode}
                curriculumName={country}
                isCurrentCurriculum
                isFooter={isFooter}
                isMenu={isMenu}
                isOnboarding={isOnboarding}
                key={`${country}-${countryCode}`}
              />
            )
          }
          // An empty placeholder would render a stray divider in the dark menu's divide-y list
          return isDarkMenu ? null : <div className="-mb-1" key={`${country}-${countryCode}`} />
        }

        return (
          <CurriculumDropdownItem
            currentCurriculumCode={currentCurriculumCode}
            curriculumCountryCode={countryCode}
            curriculumName={country}
            isDark={isDark}
            isFooter={isFooter}
            isMenu={isMenu}
            isOnboarding={isOnboarding}
            key={`${country}-${countryCode}`}
            onClick={handleOptionSelect}
            textClassName={darkMenuTextClassName}
          />
        )
      })}
    </div>
  )
}
